import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommissionContractAPI from '../api/CommissionContractAPI';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import { CardV2 } from "../components/CardV2/CardV2";
import { LabelV2 } from "../components/LabelV2/LabelV2";
import { CheckCircle } from "lucide-react";
import { useLanguageDetection } from "../hooks/useLanguageDetection";
import CONFIG from "../config/config";
import Subtitle from "../components/Subtitle/Subtitle";
import CopyText from "../components/CopyText/CopyText";
import PhoneNumberInput from "../components/PhoneNumberInput/PhoneNumberInput";
import InputShortText from "../components/InputShortText/InputShortText";
import FlyoutMenu from "../components/FlyoutMenu/FlyoutMenu";
import { isSupportedLanguage } from "../shared/supported-languages";
import SectionTitle from "../components/SectionTitle/SectionTitle";
import TextArea from "../components/TextArea/TextArea";
import InputNumber from "../components/InputNumber/InputNumber";
import PriceField from "../components/PriceField/PriceField";
import SmallCheckBox from "../components/SmallCheckBox/SmallCheckBox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/Collapsible/Collapsible";
import { Icon } from "../components/Icon/Icon";
import FileUploadWrapper from "../components/FileUploadWrapper/FileUploadWrapper";
import ListContainer from "../components/ListContainer/ListContainer";
import ElementActions from "../components/ElementActions/ElementActions";
import IconButton from "../components/IconButton/IconButton";
import { uploadFile } from '../utils/CommissionFileUploadUtils';
import { urlAndS3Encode } from "../shared/fileUtils";
import binIcon from "../assets/images/bin.svg";

interface CommissionContractInfo {
  storeInfo: any;
  template: any;
}

interface Addon {
  name: string;
  description: string;
  cost: number;
  currency: string;
}

const defaultContract = {
  brideName: '',
  brideSurname: '',
  bridePhone: '',
  brideEmail: '',
  brideSocial: '',
  dress: '',
  dressSize: '',
  purchaseYear: (new Date().getFullYear() - 1).toString(),
  purchasePriceAmount: 0,
  purchasePriceCurrency: 'EUR',
  desiredSalePriceAmount: 0,
  desiredSalePriceCurrency: 'EUR',
  damagesNotes: '',
  notes: '',
  addons: [] as Addon[],
  attachments: [] as string[],
  agreementAccepted: false,
};

const DELAY_TIME = 1500;

export default function CommissionContractPage() {
  const { contractLinkId } = useParams();
  const [contract, setContract] = useState(defaultContract);
  const [submitted, setSubmitted] = useState(false);
  const [agreementTouched, ] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});
  const [storeInfo, setStoreInfo] = useState<any>(null);
  const [contractTemplate, setContractTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [pendingFiles, setPendingFiles] = useState<{ [key: string]: { file: File; fileName: string; displayName: string } }>({});
  
  const { changeLanguage } = useLanguageDetection();

  const languages = CONFIG.SUPPORTED_LANGUAGES.map(l => {
    return {
      code: l.code,
      name: '',
      onClick: () => changeLanguage(l.code),
      icon: <Icon url={l.icon} cssStyles="w-5 rounded h-3"/>,
    }
  });

  const languageMenuItems = languages.map((lang) => ({
    text: lang.name,
    headline: lang.name,
    onClick: () => changeLanguage(lang.code),
    isDisabled: false,
    icon: lang.icon,
  }));

  const validateForm = () => {
    const errors: { [key: string]: boolean } = {};
    const requiredFields = [
      'brideName',
      'brideSurname',
      'bridePhone',
      'brideEmail',
      'dress',
      'dressSize',
      'purchaseYear',
      'purchasePriceAmount',
      'desiredSalePriceAmount',
    ];

    requiredFields.forEach(field => {
      if (!contract[field as keyof typeof contract]) {
        errors[field] = true;
      }
    });

    if (!contract.agreementAccepted) {
      errors.agreementAccepted = true;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  const removeAttachment = (fileName: string) => {
    // Remove from attachments
    setContract({
      ...contract,
      attachments: contract.attachments.filter(f => f !== fileName)
    });
    
    // Remove from pending files if it exists
    if (pendingFiles[fileName]) {
      const newPendingFiles = { ...pendingFiles };
      delete newPendingFiles[fileName];
      setPendingFiles(newPendingFiles);
    }
  };

  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress(t("commission_contract_page.processing_files") || 'Processing files...');
    
    try {
      const newPendingFiles = { ...pendingFiles };
      const newAttachmentIds: string[] = [];
      
      for (const file of files) {
        try {
          const fileWithS3Encoding = urlAndS3Encode(file.name);
          const displayFileName = decodeURI(fileWithS3Encoding);
          const fileId = `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          setUploadProgress(`${t("commission_contract_page.processing_file") || 'Processing'}: ${displayFileName}`);
          
          newPendingFiles[fileId] = {
            file,
            fileName: fileWithS3Encoding,
            displayName: displayFileName
          };
          
          newAttachmentIds.push(fileId);
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
        }
      }
      
      setPendingFiles(newPendingFiles);
      setContract({
        ...contract,
        attachments: [...contract.attachments, ...newAttachmentIds]
      });
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (!isFormValid) {
      return;
    }

    setSubmitting(true);
    setUploadProgress(t("commission_contract_page.submitting_contract") || 'Submitting contract...');
    
    try { 
      const locale = i18n.language || 'EN_US';
      
      const contractWithoutAttachments = {
        ...contract,
        attachments: []
      };
      
      const response = await CommissionContractAPI.submitContract(contractLinkId!, contractWithoutAttachments, locale);
      const newTrackingId = response.data.trackingId;
      setTrackingId(newTrackingId);
      
      // Step 2: Upload any pending files
      if (contract.attachments.length > 0) {
        setUploadProgress(t("commission_contract_page.uploading_attachments") || 'Uploading attachments...');
        
        const uploadedAttachmentUrls: string[] = [];
        
        for (const attachmentId of contract.attachments) {
          if (pendingFiles[attachmentId]) {
            const { file, fileName, displayName } = pendingFiles[attachmentId];
            
            setUploadProgress(`${t("commission_contract_page.uploading_file") || 'Uploading'}: ${displayName}`);
            
            const uploadResult = await uploadFile({
              fileName,
              file,
              trackingId: newTrackingId,
              isPublic: true
            });
            
            const fileUrl = uploadResult.url;
            
            uploadedAttachmentUrls.push(fileUrl);
          } else {
            // This is already a URL, keep it
            uploadedAttachmentUrls.push(attachmentId);
          }
        }
        
        // Step 3: Update contract with uploaded attachment URLs
        if (uploadedAttachmentUrls.length > 0) {
          setUploadProgress(t("commission_contract_page.updating_contract") || 'Updating contract...');
          
          const contractWithAttachments = {
            ...contract,
            attachments: uploadedAttachmentUrls
          };
          
          await CommissionContractAPI.updateContract(newTrackingId, contractWithAttachments);
        }
      }
      
      setSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting contract:', error);
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  useEffect(() => {
    const fetchContractInfo = async () => {
      if (!contractLinkId || contractLinkId.trim() === '') {
        setTimeout(() => {
          setError(t("commission_contract_page.errors.invalid_link"));
          setLoading(false);
        }, DELAY_TIME);
        return;
      }

      try {
        const locale = i18n.language || 'EN_US';
        const response = await CommissionContractAPI.getContractInfo(contractLinkId!, locale);
        const data = response.data as CommissionContractInfo;
        
        if (!data || !data.storeInfo || !data.template) {
          setTimeout(() => {
            setError(t("commission_contract_page.errors.store_not_found"));
            setLoading(false);
          }, DELAY_TIME);
          return;
        }
        
        setStoreInfo(data.storeInfo);
        setContractTemplate(data.template);
        
        setTimeout(() => {
          setLoading(false);
        }, DELAY_TIME);
      } catch (error) {
        setTimeout(() => {
          setError('Failed to load contract information');
          setLoading(false);
        }, DELAY_TIME);
      }
    };

    if (contractLinkId) {
      fetchContractInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractLinkId, i18n.language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {t("commission_contract_page.loading.title")}
              </h1>
              <p className="text-sm text-gray-600">
                {t("commission_contract_page.loading.message")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {t("commission_contract_page.error.title")}
              </h1>
              <p className="text-sm text-gray-600 mb-4">
                {error}
              </p>
            </div>
            
            <div className="space-y-3 text-left">
              <div className="text-xs text-gray-500">
                <p className="font-medium mb-2">{t("commission_contract_page.error.reasons.title")}</p>
                <ul className="space-y-1">
                  <li>{t("commission_contract_page.error.reasons.invalid_link")}</li>
                  <li>{t("commission_contract_page.error.reasons.store_inactive")}</li>
                  <li>{t("commission_contract_page.error.reasons.server_issue")}</li>
                </ul>
              </div>
              
              <div className="text-xs text-gray-500">
                <p className="font-medium mb-2">{t("commission_contract_page.error.actions.title")}</p>
                <ul className="space-y-1">
                  <li>{t("commission_contract_page.error.actions.check_url")}</li>
                  <li>{t("commission_contract_page.error.actions.contact_store")}</li>
                  <li>{t("commission_contract_page.error.actions.retry_later")}</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <PrimaryButton
                text={
                  <div className="inline-flex items-center">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t("commission_contract_page.error.retry")}
                  </div>
                }
                fn={() => window.location.reload()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!storeInfo || !contractTemplate) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <div className="mb-6">
              <div className="mx-auto h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {t("commission_contract_page.incomplete.title")}
              </h1>
              <p className="text-sm text-gray-600 mb-4">
                {t("commission_contract_page.incomplete.message")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <CardV2 className="max-w-4xl mx-auto">
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="mb-6">
            {/* Top row: Logo and Language */}
            <div className="flex justify-between items-center mb-4">
              {/* Store Logo */}
              {storeInfo?.logoUrl && (
                <div className="flex-shrink-0">
                  <img 
                    src={storeInfo.logoUrl} 
                    alt="Store Logo" 
                    className="h-10 w-auto max-w-32 object-contain" 
                  />
                </div>
              )}
              
              {/* Language Selector */}
              <div className="flex-shrink-0">
                <FlyoutMenu
                  menuItems={languageMenuItems}
                  trigger={
                    isSupportedLanguage(i18n.language)
                      ? languages.filter(l => l.code === i18n.language)[0]?.icon
                      : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon
                  }
                  mobileTrigger={isSupportedLanguage(i18n.language)
                    ? languages.filter(l => l.code === i18n.language)[0]?.icon
                    : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon}
                />
              </div>
            </div>
            
            {/* Second row: Page Title - Full width on mobile */}
            <div className="text-center">
              <Subtitle text={t("commission_contract_page.title") || "Fill in commission contract data"} />
            </div>
          </div>

          {/* Store Info - Compact */}
          {(storeInfo?.brandName || storeInfo?.companyName || storeInfo?.companyAddress || storeInfo?.deliveryAddress || storeInfo?.phoneNumber || storeInfo?.email || storeInfo?.instagram) && (
            <div className="text-center text-sm text-muted-foreground mb-6">
              <div className="font-semibold text-gray-700 mb-1">{storeInfo?.brandName || storeInfo?.companyName}</div>
              <div className="text-xs text-gray-500 mb-2">{storeInfo?.companyAddress || storeInfo?.deliveryAddress}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center text-xs">
                {storeInfo?.phoneNumber && <span className="text-gray-600">{storeInfo.phoneNumber}</span>}
                {storeInfo?.email && <span className="text-gray-600">{storeInfo.email}</span>}
                {storeInfo?.instagram && <a href={`https://instagram.com/${storeInfo.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">{storeInfo.instagram}</a>}
              </div>
            </div>
          )}
          {submitted ? (
            <div className="max-w-4xl mx-auto">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {t("commission_contract_page.success.title") || "Thank you for submitting your data!"}
                </h1>
                <p className="text-base text-gray-600 max-w-xl mx-auto">
                  {t("commission_contract_page.success.message") || "Your form has been submitted successfully. We will contact you soon to proceed with the next steps."}
                </p>
              </div>

              {/* Success Content Cards */}
               <div className="space-y-6">
                 {/* What Happens Next Card */}
                 <div className="bg-white rounded-lg border border-gray-200 p-6">
                                        <div className="flex items-center mb-4">
                       <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#fda9a9' }}>
                         <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                         </svg>
                       </div>
                       <h2 className="text-xl font-semibold text-gray-900">
                         {t("commission_contract_page.success.next_steps.title") || "What happens next?"}
                       </h2>
                     </div>
                   
                   <div className="space-y-4">
                     <div className="flex items-start space-x-3">
                       <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#fda9a9' }}></div>
                       <p className="text-gray-700">
                         {t("commission_contract_page.success.next_steps.email_confirmation") || "You'll receive a confirmation email for your application"}
                       </p>
                     </div>
                     <div className="flex items-start space-x-3">
                       <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#fda9a9' }}></div>
                       <p className="text-gray-700">
                         {t("commission_contract_page.success.next_steps.photos_request") || "We may request photos of the dress for evaluation"}
                       </p>
                     </div>
                     <div className="flex items-start space-x-3">
                       <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#fda9a9' }}></div>
                       <p className="text-gray-700">
                         {t("commission_contract_page.success.next_steps.listing_fee") || "If applicable, you may need to pay a listing fee"}
                       </p>
                     </div>
                     <div className="flex items-start space-x-3">
                       <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#fda9a9' }}></div>
                       <p className="text-gray-700">
                         {t("commission_contract_page.success.next_steps.communication") || "We'll keep you updated throughout the process"}
                       </p>
                     </div>
                   </div>
                 </div>

                 {/* Tracking Information Card */}
                 <div className="bg-white rounded-lg border border-gray-200 p-6">
                   <div className="flex items-center mb-4">
                     <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#fda9a9' }}>
                       <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                       </svg>
                     </div>
                     <h2 className="text-xl font-semibold text-gray-900">
                       {t("commission_contract_page.success.tracking.title") || "Track Your Application"}
                     </h2>
                   </div>
                   
                   <p className="text-gray-600 mb-4">
                     {t("commission_contract_page.success.tracking.description") || "Monitor your application status, communicate with the salon, and update information anytime."}
                   </p>

                   {trackingId ? (
                     <div className="space-y-3">
                       <p className="text-sm font-medium text-gray-700">
                         {t("commission_contract_page.success.tracking_link_label") || "Your personal tracking link:"}
                       </p>
                       <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border">
                         <code className="flex-1 text-sm font-mono text-gray-800 break-all">
                           {`${window.location.origin}/drops/track/${trackingId}`}
                         </code>
                         <CopyText text={`${window.location.origin}/drops/track/${trackingId}`} />
                       </div>
                       <p className="text-xs text-gray-500">
                         {t("commission_contract_page.success.tracking.save_link") || "Save this link to check your application status anytime"}
                       </p>
                     </div>
                   ) : (
                     <div className="flex items-center justify-center py-4">
                       <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                       <span className="ml-2 text-gray-500">
                         {t("commission_contract_page.success.loading_link") || "Generating your tracking link..."}
                       </span>
                     </div>
                   )}
                 </div>

                 {/* Application Summary Card */}
                 <div className="bg-white rounded-lg border border-gray-200 p-6">
                   <div className="flex items-center mb-4">
                     <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: '#fda9a9' }}>
                       <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                       </svg>
                     </div>
                     <h3 className="text-lg font-semibold text-gray-900">
                       {t("commission_contract_page.success.summary.title") || "Application Summary"}
                     </h3>
                   </div>
                   
                                        <div className="space-y-2 text-sm">
                       <div className="flex justify-between">
                         <span className="text-gray-600">{t("commission_contract_page.success.summary.item") || "Item:"}</span>
                         <span className="font-medium text-gray-900">{contract.dress}</span>
                       </div>
                       <div className="flex justify-between">
                         <span className="text-gray-600">{t("commission_contract_page.success.summary.size") || "Size:"}</span>
                         <span className="font-medium text-gray-900">{contract.dressSize}</span>
                       </div>
                       <div className="flex justify-between">
                         <span className="text-gray-600">{t("commission_contract_page.success.summary.purchase_price") || "Purchase Price:"}</span>
                         <span className="font-medium text-gray-900">
                           {contract.purchasePriceAmount} {contract.purchasePriceCurrency}
                         </span>
                       </div>
                       <div className="flex justify-between">
                         <span className="text-gray-600">{t("commission_contract_page.success.summary.desired_price") || "Desired Sale Price:"}</span>
                         <span className="font-medium text-gray-900">
                           {contract.desiredSalePriceAmount} {contract.desiredSalePriceCurrency}
                         </span>
                       </div>
                     </div>
                 </div>
               </div>

                             {/* Bottom CTA */}
               <div className="text-center mt-8 pt-6 border-t border-gray-200">
                 <p className="text-gray-600 mb-4">
                   {t("commission_contract_page.success.bottom_message") || "Your application is now being processed. We'll be in touch soon!"}
                 </p>
                 <div className="flex justify-center">
                   <PrimaryButton
                     text={t("commission_contract_page.success.view_tracking") || "View Tracking"}
                     fn={() => window.open(`${window.location.origin}/drops/track/${trackingId}`, '_blank')}
                     isDisabled={!trackingId}
                   />
                 </div>
               </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.customer_data") || "Customer data"} clazz="text-xl mt-6" />
                </div>
                <div>
                  <LabelV2 htmlFor="brideName">{t("commission_contract_page.form.first_name") || "First name *"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.brideName}
                    onChange={e => setContract({ ...contract, brideName: e.target.value })}
                    validationFn={(value) => value.length > 0}
                    errorMessage="errors.required_field"
                    isDisabled={false}
                  />
                  {fieldErrors.brideName && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div>
                  <LabelV2 htmlFor="brideSurname">{t("commission_contract_page.form.last_name") || "Last name *"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.brideSurname}
                    onChange={e => setContract({ ...contract, brideSurname: e.target.value })}
                    validationFn={(value) => value.length > 0}
                    errorMessage="errors.required_field"
                    isDisabled={false}
                  />
                  {fieldErrors.brideSurname && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div>
                  <LabelV2 htmlFor="bridePhone">{t("commission_contract_page.form.phone") || "Phone number *"}</LabelV2>
                  <PhoneNumberInput 
                    defaultValue={contract.bridePhone}
                    onChange={value => setContract({ ...contract, bridePhone: value })}
                    validationFn={(value) => value.length >= 8}
                    isDisabled={false}
                  />
                  {fieldErrors.bridePhone && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div>
                  <LabelV2 htmlFor="brideEmail">{t("commission_contract_page.form.email") || "Email address *"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.brideEmail}
                    onChange={e => setContract({ ...contract, brideEmail: e.target.value })}
                    validationFn={(value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)}
                    errorMessage="errors.invalid_email"
                    isDisabled={false}
                  />
                  {fieldErrors.brideEmail && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div className="md:col-span-2">
                  <LabelV2 htmlFor="brideSocial">{t("commission_contract_page.form.social_media") || "Instagram or Facebook nickname"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.brideSocial}
                    onChange={e => setContract({ ...contract, brideSocial: e.target.value })}
                    isDisabled={false}
                  />
                </div>

                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.item_information") || "Information about the item"} clazz="text-xl mt-6" />
                </div>
                <div>
                  <LabelV2 htmlFor="dress">{t("commission_contract_page.form.dress_model") || "Dress model (or Salon) *"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.dress}
                    onChange={e => setContract({ ...contract, dress: e.target.value })}
                    validationFn={(value) => value.length > 0}
                    errorMessage="errors.required_field"
                    isDisabled={false}
                  />
                  {fieldErrors.dress && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div>
                  <LabelV2 htmlFor="dressSize">{t("commission_contract_page.form.dress_size") || "Dress size *"}</LabelV2>
                  <InputShortText 
                    defaultValue={contract.dressSize}
                    onChange={e => setContract({ ...contract, dressSize: e.target.value })}
                    placeholder={t("commission_contract_page.form.dress_size_placeholder") || "e.g. 36, 38, 40"}
                    validationFn={(value) => value.length > 0}
                    errorMessage="errors.required_field"
                    isDisabled={false}
                  />
                  {fieldErrors.dressSize && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div className="md:col-span-2">
                  <LabelV2 htmlFor="dressSize">{t("commission_contract_page.form.dress_measurements") || "Dress measurements"}</LabelV2>
                  <TextArea 
                    defaultValue={contract.notes}
                    onChange={e => setContract({ ...contract, notes: e.target.value })}
                    placeholder={t("commission_contract_page.form.dress_measurements_placeholder") || "Bust:.................................................................................................\nWaist:.................................................................................................\nHips: .............................................................................................\nHeight: ..............................................................................................."}
                    isDisabled={false}
                    helperText={t("commission_contract_page.form.dress_measurements_helper") || "Enter exact dress measurements to help match future clients"}
                  />
                </div>
                <div>
                  <LabelV2 htmlFor="purchaseYear">{t("commission_contract_page.form.purchase_year") || "Purchase year *"}</LabelV2>
                  <InputNumber 
                    defaultValue={parseInt(contract.purchaseYear || '0')}
                    onChange={e => setContract({ ...contract, purchaseYear: e.target.value })}
                    isDisabled={false}
                    validationFn={(value) => value >= 1900 && value <= new Date().getFullYear()}
                    errorMessage="errors.invalid_year"
                  />
                  {fieldErrors.purchaseYear && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div>
                  <LabelV2 htmlFor="purchasePriceAmount">{t("commission_contract_page.form.purchase_price") || "Purchase price in salon *"}</LabelV2>
                  <PriceField
                    label=""
                    currency={contract.purchasePriceCurrency}
                    price={contract.purchasePriceAmount}
                    currencies={CONFIG.SUPPORTED_CURRENCIES}
                    onCurrencyChange={currency => setContract({ ...contract, purchasePriceCurrency: currency })}
                    onPriceChange={price => setContract({ ...contract, purchasePriceAmount: price })}
                    isDisabled={false}
                  />
                  {fieldErrors.purchasePriceAmount && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div className="md:col-span-2">
                  <LabelV2 htmlFor="desiredSalePriceAmount">{t("commission_contract_page.form.sale_price_range") || "Sale price range you're interested in *"}</LabelV2>
                  <PriceField
                    label=""
                    currency={contract.desiredSalePriceCurrency}
                    price={contract.desiredSalePriceAmount}
                    currencies={CONFIG.SUPPORTED_CURRENCIES}
                    onCurrencyChange={currency => setContract({ ...contract, desiredSalePriceCurrency: currency })}
                    onPriceChange={price => setContract({ ...contract, desiredSalePriceAmount: price })}
                    isDisabled={false}
                  />
                  {fieldErrors.desiredSalePriceAmount && <div className="text-xs text-red-500 mt-1">{t("commission_contract_page.form.field_required") || "This field is required."}</div>}
                </div>
                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.dress_photos") || "Dress Photos"} clazz="text-xl mt-6" />
                  <div className="text-xs text-muted-foreground mb-4">
                    {t("commission_contract_page.form.photos_description") || "Upload photos of the dress to help with evaluation. This is optional but recommended."}
                  </div>
                  
                  {/* File Upload Section */}
                  <div className="mb-4">
                    <FileUploadWrapper
                      label={t("commission_contract_page.form.add_dress_photos") || "Add dress photos"}
                      onChange={handleFileChange}
                      allowedMultipleFiles={true}
                      maxSize={CONFIG.PUBLIC_MAX_FILE_UPLOAD}
                      allowedTypes={CONFIG.FILE_TYPES}
                      isDisabled={submitting}
                    />
                    {isUploading && (
                      <div className="mt-2 text-sm text-blue-600 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        {uploadProgress || (t("commission_contract_page.uploading_files") || 'Uploading files...')}
                      </div>
                    )}
                  </div>
                  
                  {/* Attachments List */}
                  {contract.attachments.length > 0 && (
                    <div className="mt-4">
                      <LabelV2>{t("commission_contract_page.form.uploaded_files") || "Uploaded files"}</LabelV2>
                      <ListContainer
                        elements={contract.attachments}
                        toElementFn={(attachmentId: string, i: number) => {
                          const isPendingFile = pendingFiles[attachmentId];
                          const displayName = isPendingFile 
                            ? isPendingFile.displayName 
                            : attachmentId.split('/').pop() || attachmentId;
                          
                          return (
                            <ElementActions
                              actions={
                                <div>
                                  {!submitting && (
                                    <IconButton onClick={() => removeAttachment(attachmentId)}>
                                      <img src={binIcon} alt="Remove" className="w-5 h-5" />
                                    </IconButton>
                                  )}
                                </div>
                              }
                            >
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm text-gray-700 truncate">
                                  {displayName}
                                </span>
                              </div>
                            </ElementActions>
                          );
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.additional_information") || "Additional Information"} clazz="text-xl mt-6" />
                </div>
                <div className="md:col-span-2">
                  <TextArea 
                    label={t("commission_contract_page.form.damages_notes") || "List of damages or notes before acceptance"}
                    defaultValue={contract.damagesNotes}
                    onChange={e => setContract({ ...contract, damagesNotes: e.target.value })}
                    maxLength={200}
                    placeholder={t("commission_contract_page.form.damages_placeholder") || "Describe any damages, stains, or other notes about the dress condition..."}
                    isDisabled={false}
                  />
                </div>

                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.addons") || "Add-ons"} clazz="text-xl mt-6" />
                  <div className="text-xs text-muted-foreground mb-4">
                    {t("commission_contract_page.form.addons_description") || "Add additional items (e.g. veil, jewelry) along with their costs"}
                  </div>
                  {contract.addons.map((addon, index) => (
                    <div key={index} className="border rounded p-4 mb-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <LabelV2 htmlFor={`addon-name-${index}`}>{t("commission_contract_page.form.addon_name") || "Add-on name *"}</LabelV2>
                          <InputShortText 
                            defaultValue={addon.name}
                            onChange={e => {
                              const newAddons = [...contract.addons];
                              newAddons[index].name = e.target.value;
                              setContract({ ...contract, addons: newAddons });
                            }}
                            placeholder={t("commission_contract_page.form.addon_name_placeholder") || "e.g. Veil, Jewelry"}
                            isDisabled={false}
                          />
                        </div>
                        <div>
                          <LabelV2 htmlFor={`addon-description-${index}`}>{t("commission_contract_page.form.addon_description") || "Description"}</LabelV2>
                          <InputShortText 
                            defaultValue={addon.description}
                            onChange={e => {
                              const newAddons = [...contract.addons];
                              newAddons[index].description = e.target.value;
                              setContract({ ...contract, addons: newAddons });
                            }}
                            placeholder={t("commission_contract_page.form.addon_description_placeholder") || "Brief description of the add-on"}
                            isDisabled={false}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <LabelV2 htmlFor={`addon-cost-${index}`}>{t("commission_contract_page.form.addon_cost") || "Original price"}</LabelV2>
                            <PriceField
                              label=""
                              currency={addon.currency}
                              price={addon.cost}
                              currencies={CONFIG.SUPPORTED_CURRENCIES}
                              onCurrencyChange={currency => {
                                const newAddons = [...contract.addons];
                                newAddons[index].currency = currency;
                                setContract({ ...contract, addons: newAddons });
                              }}
                              onPriceChange={price => {
                                const newAddons = [...contract.addons];
                                newAddons[index].cost = price;
                                setContract({ ...contract, addons: newAddons });
                              }}
                              isDisabled={false}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newAddons = contract.addons.filter((_, i) => i !== index);
                              setContract({ ...contract, addons: newAddons });
                            }}
                            className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors"
                            title={t("commission_contract_page.form.remove_addon") || "Remove add-on"}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newAddon: Addon = {
                        name: '',
                        description: '',
                        cost: 0,
                        currency: 'EUR'
                      };
                      setContract({ ...contract, addons: [...contract.addons, newAddon] });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    {t("commission_contract_page.form.add_addon") || "+ Add add-on"}
                  </button>
                </div>

                <div className="md:col-span-2">
                  <SectionTitle text={t("commission_contract_page.sections.terms_conditions") || "Terms and Conditions"} clazz="text-xl mt-6" />
                </div>
                <div className="md:col-span-2">
                  {/* Fee info box, now left-aligned and less prominent */}
                  <div className="mb-2 text-xs text-muted-foreground text-left">
                    <div><b>{t("commission_contract_page.form.fee_info.drop_fee") || "Listing fee:"}</b> {(!contractTemplate?.dropFee || contractTemplate.dropFee.price === 0) ? t("commission_contract_page.form.fee_info.free") || 'Free' : `${contractTemplate.dropFee.price} ${contractTemplate.dropFee.currency}`}</div>
                    <div><b>{t("commission_contract_page.form.fee_info.renewal_fee") || "Renewal fee:"}</b> {(!contractTemplate?.renewalFee || contractTemplate.renewalFee.price === 0) ? t("commission_contract_page.form.fee_info.free") || 'Free' : `${contractTemplate.renewalFee.price} ${contractTemplate.renewalFee.currency}`}</div>
                  </div>
                  
                  {/* Form Conditions - replacing the contract conditions */}
                  {contractTemplate?.formConditions && contractTemplate.formConditions.length > 0 && (
                    <div className="mt-2">
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="underline text-blue-600 cursor-pointer">{t("commission_contract_page.form.show_hide_terms") || "Show/hide terms"}</CollapsibleTrigger>
                        <CollapsibleContent className="border rounded bg-white p-4 mt-2 max-h-96 overflow-y-auto">
                          <ul className="list-disc pl-5">
                            {contractTemplate.formConditions.map((condition: string, idx: number) => (
                              <li className="pt-2 pb-2" key={idx}>{condition}</li>
                            ))}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  )}
                  
                  <SmallCheckBox
                    label={`${t("commission_contract_page.form.agreement_label") || "I confirm that I have read the"} ${t("commission_contract_page.form.agreement_helper") || "Terms and Conditions (read the terms) and I accept its provisions."}`}
                    onChange={e => setContract({ ...contract, agreementAccepted: e.target.checked })}
                    defaultValue={contract.agreementAccepted}
                    errorMessage={agreementTouched && !contract.agreementAccepted ? t("commission_contract_page.form.agreement_error") || "You must accept the terms and conditions to submit the form." : ''}
                  />
                </div>
              </div>
              <div className="flex justify-center mt-6">
                <PrimaryButton 
                  text={submitting ? t("commission_contract_page.form.submitting") || "Submitting..." : t("commission_contract_page.form.submit") || "Submit"} 
                  fn={handleSubmit} 
                  isDisabled={!isFormValid || submitting || isUploading} 
                />
              </div>
            </form>
          )}
        </div>
      </CardV2>
    </div>
  );
} 